import * as React from 'react';
import { AccordionItem } from './Item/AccordionItem';
import { classes, st } from './Accordion.st.css';
import { KEYS } from '../../common/keyCodes';
import { AccordionFocusElement, AccordionSize, } from './types';
const expandAnimationDuration = 300;
/** A component showing a list of expandable/collapsible items */
export class Accordion extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            items: [],
            itemRefs: [],
            focusedItem: null,
        };
        this.rootRef = React.createRef();
        this.componentDidMount = () => {
            this.initItems();
        };
        this.componentDidUpdate = (prevProps) => {
            var _a;
            if (!prevProps) {
                return;
            }
            const currentChildren = React.Children.toArray(this.props.children);
            const hasChildrenChanged = (_a = React.Children.map(prevProps.children, (child) => child.props)) === null || _a === void 0 ? void 0 : _a.some((props, index) => {
                var _a;
                return ((_a = currentChildren[index]) === null || _a === void 0 ? void 0 : _a.props) &&
                    currentChildren[index].props.id !== props.id;
            });
            if (hasChildrenChanged) {
                this.initItems();
            }
        };
        this.handleArrowDown = () => {
            const { focusedItem, itemRefs } = this.state;
            if (!focusedItem || focusedItem.element !== AccordionFocusElement.Header) {
                return null;
            }
            return {
                index: (focusedItem.index + 1) % itemRefs.length,
                element: AccordionFocusElement.Header,
            };
        };
        this.handleArrowUp = () => {
            const { focusedItem, itemRefs } = this.state;
            if (!focusedItem || focusedItem.element !== AccordionFocusElement.Header) {
                return null;
            }
            const previousItemIndex = (((focusedItem.index - 1) % itemRefs.length) + itemRefs.length) %
                itemRefs.length;
            return {
                index: previousItemIndex,
                element: AccordionFocusElement.Header,
            };
        };
        this.focusItem = (focusItem) => {
            var _a, _b, _c, _d;
            if (!focusItem) {
                return;
            }
            if (focusItem.element === AccordionFocusElement.Content) {
                (_b = (_a = this.state.itemRefs[focusItem.index].contentRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus();
            }
            else {
                (_d = (_c = this.state.itemRefs[focusItem.index].headerRef) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.focus();
            }
            this.setState({
                focusedItem: focusItem,
            });
        };
        this.handledEvents = [
            KEYS.ArrowUp,
            KEYS.ArrowDown,
            KEYS.Home,
            KEYS.End,
            KEYS.Spacebar,
            KEYS.Enter,
        ];
        this._isEventOnAccordionItemContent = (event) => {
            var _a, _b;
            const { focusedItem, itemRefs } = this.state;
            const focusedItemRefs = itemRefs[focusedItem.index];
            const isInnerContent = (_a = focusedItemRefs === null || focusedItemRefs === void 0 ? void 0 : focusedItemRefs.contentRef) === null || _a === void 0 ? void 0 : _a.current.contains(event.target);
            const isAccordionItem = event.target === ((_b = focusedItemRefs === null || focusedItemRefs === void 0 ? void 0 : focusedItemRefs.contentRef) === null || _b === void 0 ? void 0 : _b.current);
            return isInnerContent && !isAccordionItem;
        };
        this.onKeyDown = (event) => {
            const isInnerContent = this._isEventOnAccordionItemContent(event);
            if (isInnerContent) {
                return;
            }
            if (this.handledEvents.includes(event.key)) {
                event.preventDefault();
            }
        };
        this.onKeyUp = (event) => {
            const { multiple } = this.props;
            const { focusedItem, items } = this.state;
            const isInnerContent = this._isEventOnAccordionItemContent(event);
            if (isInnerContent) {
                return;
            }
            if (this.handledEvents.includes(event.key)) {
                event.preventDefault();
            }
            if (event.key === KEYS.ArrowDown) {
                this.focusItem(this.handleArrowDown());
            }
            if (event.key === KEYS.ArrowUp) {
                this.focusItem(this.handleArrowUp());
            }
            if (event.key === KEYS.Home) {
                this.focusItem({
                    index: 0,
                    element: AccordionFocusElement.Header,
                });
            }
            if (event.key === KEYS.End) {
                this.focusItem({
                    index: this.state.itemRefs.length - 1,
                    element: AccordionFocusElement.Header,
                });
            }
            if (event.key === KEYS.Escape) {
                if (multiple) {
                    this.closeAllItems();
                }
                else if (items[focusedItem.index].isExpanded) {
                    this.closeItem(items[focusedItem.index].id);
                }
            }
            if (event.key === KEYS.Spacebar || event.key === KEYS.Enter) {
                this.handleItemClick(items[focusedItem.index].id);
            }
        };
        this.onMouseDown = (event) => event.preventDefault();
        this.onFocus = (event) => {
            const { itemRefs } = this.state;
            const focusedHeaderIdx = itemRefs.findIndex(({ headerRef }) => (headerRef === null || headerRef === void 0 ? void 0 : headerRef.current) === event.target);
            const focusedContentIdx = itemRefs.findIndex(({ contentRef }) => {
                var _a;
                const isItemFocused = (contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) === event.target;
                const isItemContentFocused = (_a = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target);
                return isItemFocused || isItemContentFocused;
            });
            if (focusedHeaderIdx !== -1) {
                this.setState({
                    focusedItem: {
                        index: focusedHeaderIdx,
                        element: AccordionFocusElement.Header,
                    },
                });
            }
            else if (focusedContentIdx !== -1) {
                this.setState({
                    focusedItem: {
                        index: focusedContentIdx,
                        element: AccordionFocusElement.Content,
                    },
                });
            }
        };
        this.onBlur = (event) => {
            var _a, _b;
            const contains = (_b = (_a = this.rootRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.contains(event.relatedTarget);
            if (!contains) {
                this.setState({
                    focusedItem: null,
                });
            }
        };
        this.handleItemClick = (id) => {
            const { onItemClick } = this.props;
            const { items } = this.state;
            const clickedItem = items.find((item) => item.id === id);
            onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick(id, !(clickedItem === null || clickedItem === void 0 ? void 0 : clickedItem.isExpanded));
            if (clickedItem === null || clickedItem === void 0 ? void 0 : clickedItem.isExpanded) {
                this.closeItem(id);
            }
            else {
                this.expandItem(id);
            }
        };
        this.expandItem = (id) => {
            const { items } = this.state;
            this.setItemExpanded(id, true);
            const expandedItemIndex = items.findIndex((item) => item.id === id);
            setTimeout(() => this.focusItem({
                index: expandedItemIndex,
                element: AccordionFocusElement.Content,
            }), expandAnimationDuration);
        };
        this.closeItem = (id) => {
            const { items } = this.state;
            this.setItemExpanded(id, false);
            const closedItemIndex = items.findIndex((item) => item.id === id);
            this.focusItem({
                index: closedItemIndex,
                element: AccordionFocusElement.Header,
            });
        };
        this.setItemExpanded = (id, itemExpanded) => {
            const { items } = this.state;
            const { multiple } = this.props;
            const toggledItems = items.map((item) => ({
                id: item.id,
                isExpanded: item.id === id ? itemExpanded : multiple ? item.isExpanded : false,
            }));
            this.setState({
                items: toggledItems,
            });
        };
        this.closeAllItems = () => {
            const { items, focusedItem } = this.state;
            const toggledItems = items.map((item) => ({
                id: item.id,
                isExpanded: false,
            }));
            this.focusItem({
                index: focusedItem.index,
                element: AccordionFocusElement.Header,
            });
            this.setState({
                items: toggledItems,
            });
        };
        this.initItems = () => {
            var _a;
            const { initiallyExpanded, children } = this.props;
            const items = (_a = React.Children.map(children, (child) => {
                if (!React.isValidElement(child)) {
                    return null;
                }
                const isInitiallyExpanded = typeof initiallyExpanded === 'string'
                    ? initiallyExpanded === child.props.id
                    : initiallyExpanded === null || initiallyExpanded === void 0 ? void 0 : initiallyExpanded.some((id) => id === child.props.id);
                return {
                    id: child.props.id,
                    isExpanded: isInitiallyExpanded,
                };
            })) === null || _a === void 0 ? void 0 : _a.filter((item) => item);
            this.setState({
                items,
                itemRefs: items === null || items === void 0 ? void 0 : items.map(() => ({
                    headerRef: React.createRef(),
                    contentRef: React.createRef(),
                })),
            });
        };
        this.render = () => {
            const { className, children, size, 'aria-label': ariaLabel } = this.props;
            const { items, itemRefs } = this.state;
            return (React.createElement("div", { className: st(classes.root, {}, className), "data-hook": this.props['data-hook'], role: "region", "aria-label": ariaLabel, onMouseDown: this.onMouseDown, onKeyDown: this.onKeyDown, onKeyUp: this.onKeyUp, onFocus: this.onFocus, onBlur: this.onBlur, ref: this.rootRef }, React.Children.map(children, (child, index) => {
                var _a;
                if (!React.isValidElement(child)) {
                    return child;
                }
                const isExpanded = !!((_a = items.find((item) => item.id === child.props.id)) === null || _a === void 0 ? void 0 : _a.isExpanded);
                return React.cloneElement(child, Object.assign({ isExpanded,
                    size, onClick: () => this.handleItemClick(child.props.id), className: classes.itemContent }, itemRefs[index]));
            })));
        };
    }
}
Accordion.displayName = 'Accordion';
Accordion.defaultProps = {
    multiple: false,
    size: AccordionSize.Small,
};
Accordion.Item = AccordionItem;
//# sourceMappingURL=Accordion.js.map